import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  BotaoStep,
  Carregando,
  FormRef,
  FormUnform,
  scrollParaTopo,
  SelectOpcao,
  SelectUnform,
  TelefoneInputUnform,
  TextAreaUnform,
  WizardContainer
} from 'src/componentes'
import { mensagemPadrao } from 'src/dados-estaticos'
import { Api } from 'src/servicos'

import RescisaoContainer from '../../container'
import { ContainerAcoes, ContainerConteudo } from '../../styles'
import { DadosSolicitacaoWizard } from '../../tipos'
import { schema } from './schema'

const TEXTO_MOTIVO_OUTRO = 'Outro'

interface FormDados {
  matriculaId: string
  telefone: string
  motivoId: string
  motivoDetalhado: string
  motivoOutroTexto?: string
}

/**
 * TELA 3 — Dados da solicitação (§3 da PROPOSTA).
 * Vem antes da TELA 2 (Valores) no wizard: o backend só calcula os valores
 * depois que o aluno escolhe o curso/matrícula aqui (PreencherDadosSolicitacao
 * vincula a Matricula ao protocolo, pré-requisito de ObterValoresRescisaoQuery).
 */
export const EtapaDados: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const formRef = useRef<FormRef>(null)
  const {
    protocolo,
    dadosSolicitacao,
    definirPasso,
    definirDadosSolicitacao
  } = RescisaoContainer.useContainer()
  const { mudarEtapa, voltarWizard } = WizardContainer.useContainer()
  const [motivos, definirMotivos] = useState<SelectOpcao[]>([])
  const [matriculas, definirMatriculas] = useState<SelectOpcao[]>([])
  const [carregandoOpcoes, definirCarregandoOpcoes] = useState(true)
  const [carregando, definirCarregando] = useState(false)
  const [motivoOutroSelecionado, definirMotivoOutroSelecionado] = useState(
    false
  )

  const telefoneCadastrado = dadosSolicitacao?.telefone ?? protocolo?.telefone

  useEffect(() => {
    scrollParaTopo()

    const carregar = async () => {
      try {
        const opcoesMotivos = await Api.ListarMotivosRescisaoOpcoesAluno()
        definirMotivos(opcoesMotivos)

        const opcoesMatriculas = await Api.ListarMatriculasAtivasAluno(id)
        definirMatriculas(opcoesMatriculas)

        const motivoSalvo = opcoesMotivos.find(
          m => m.id === dadosSolicitacao?.motivoId
        )
        const ehMotivoOutro = motivoSalvo?.texto === TEXTO_MOTIVO_OUTRO
        definirMotivoOutroSelecionado(ehMotivoOutro)

        if (ehMotivoOutro && dadosSolicitacao?.motivoOutroTexto) {
          formRef.current?.setFieldValue(
            'motivoOutroTexto',
            dadosSolicitacao.motivoOutroTexto
          )
        }

        if (telefoneCadastrado) {
          formRef.current?.setFieldValue('telefone', telefoneCadastrado)
        }
      } finally {
        definirCarregandoOpcoes(false)
      }
    }

    carregar()
  }, [id]) //eslint-disable-line

  const aoAlterarMotivo = (opcao: SelectOpcao) => {
    definirMotivoOutroSelecionado(opcao?.texto === TEXTO_MOTIVO_OUTRO)
  }

  const acaoSucesso = async (dados: FormDados) => {
    const erros: Record<string, string> = {}

    if (!dados.telefone) {
      erros.telefone = mensagemPadrao
    }

    if (motivoOutroSelecionado && !dados.motivoOutroTexto) {
      erros.motivoOutroTexto = mensagemPadrao
    }

    if (Object.keys(erros).length > 0) {
      formRef.current?.setErrors(erros)
      return
    }

    try {
      definirCarregando(true)

      const payload: DadosSolicitacaoWizard = {
        matriculaId: dados.matriculaId,
        motivoId: dados.motivoId,
        motivoDetalhado: dados.motivoDetalhado,
        motivoOutroTexto: motivoOutroSelecionado
          ? dados.motivoOutroTexto
          : undefined,
        telefone: dados.telefone
      }

      await Api.PreencherDadosSolicitacao(id, {
        matriculaId: payload.matriculaId,
        motivoId: payload.motivoId,
        motivoDetalhado: payload.motivoDetalhado,
        motivoOutroTexto: payload.motivoOutroTexto,
        telefone: payload.telefone
      })

      definirDadosSolicitacao(payload)
      mudarEtapa(3)
      definirPasso(3)
    } finally {
      definirCarregando(false)
    }
  }

  if (carregandoOpcoes) {
    return <Carregando texto="Carregando dados da solicitação..." />
  }

  return (
    <ContainerConteudo>
      <h2>Dados da solicitação</h2>
      <FormUnform
        ref={formRef}
        schema={schema}
        dadosIniciais={dadosSolicitacao}
        acaoSucesso={acaoSucesso}
      >
        <SelectUnform
          id="slct_matricula_rescisao"
          name="matriculaId"
          label="Qual curso/turma deseja solicitar a rescisão?"
          opcoes={matriculas}
          obrigatorio
        />

        <TelefoneInputUnform
          name="telefone"
          label="Telefone para contato"
          obrigatorio
        />

        <SelectUnform
          id="slct_motivo_rescisao"
          name="motivoId"
          label="Qual o principal motivo da sua solicitação?"
          opcoes={motivos}
          valorAlterado={aoAlterarMotivo}
          obrigatorio
        />

        {motivoOutroSelecionado && (
          <TextAreaUnform
            id="txt_motivo_outro"
            name="motivoOutroTexto"
            label="Descreva o motivo da sua solicitação"
            maxLength={500}
            obrigatorio
          />
        )}

        <TextAreaUnform
          id="txt_motivo_detalhado"
          name="motivoDetalhado"
          label="Conte-nos mais sobre o motivo da sua solicitação"
          maxLength={2000}
          obrigatorio
        />
        <ContainerAcoes>
          <BotaoStep
            invertido
            texto="Voltar para"
            textoNegrito="Avisos"
            tema="Secundario"
            type="button"
            onClick={() => voltarWizard()}
          />
          <BotaoStep
            texto="Ir para"
            textoNegrito="Valores da Rescisão"
            type="submit"
            carregando={carregando}
          />
        </ContainerAcoes>
      </FormUnform>
    </ContainerConteudo>
  )
}
